import PlusIcon from '@heroicons/react/24/outline/PlusIcon';
import { useMemo, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Loader } from '@/components/core/Loader';
import { useTranslation } from '@/hooks/useTranslation';
import type { AssetLabel } from '@/pages/dam/api';
import { filterLabelsByName, findLabelByName, sortLabelsAlphabetically } from '@/pages/dam/utils/damTags';

import { DamSidePanel } from '../DamSidePanel';
import { DamTagNameField } from '../DamTagNameField';
import { DamTagRow } from '../DamTagRow';
import { DamTagSortMenu } from '../DamTagSortMenu';

interface DamTagsPanelProps {
    labels: AssetLabel[];
    isLoading: boolean;
    selectedLabelIds: string[];
    onToggleLabel: (labelId: string) => void;
    onClose: () => void;
    /** Creates a tag; resolves `false` when it failed. */
    onCreateTag: (name: string) => Promise<boolean>;
    /** Renames a tag; resolves `false` when it failed. */
    onRenameTag: (label: AssetLabel, name: string) => Promise<boolean>;
    /** Asks for confirmation before the tag is deleted. */
    onDeleteTag: (label: AssetLabel) => void;
}

/**
 * Tags panel of the library (wireframe `I6392:31621;6476:92549`, tag menu `6880:168354`):
 * tag count and sort menu, "Create new tag", then the tags with the number of assets
 * carrying them (global count from `GET /labels`, D-035). Several tags are OR-ed.
 * "Create new tag" opens "Type tag name ..." (`7170:103385`), which also filters the
 * list; Enter or the button creates the tag. Edit and delete per tag (D-038).
 */
function DamTagsPanel({ labels, isLoading, selectedLabelIds, onToggleLabel, onClose, onCreateTag, onRenameTag, onDeleteTag }: DamTagsPanelProps) {
    const { t } = useTranslation();
    const [newName, setNewName] = useState<string | null>(null);
    const [createError, setCreateError] = useState<string>();
    const [isCreating, setIsCreating] = useState(false);
    const sortedLabels = useMemo(() => sortLabelsAlphabetically(labels), [labels]);
    const visibleLabels = useMemo(() => filterLabelsByName(sortedLabels, newName ?? ''), [sortedLabels, newName]);
    const tagCount = labels.length === 1
        ? t('DAM.LIBRARY.TAG_COUNT_ONE')
        : t('DAM.LIBRARY.TAG_COUNT', { count: labels.length.toLocaleString() });

    const validateName = (name: string, exceptId?: string) =>
        (findLabelByName(labels, name, exceptId) ? t('DAM.TAGS.ALREADY_EXISTS') : undefined);

    const closeCreate = () => {
        setNewName(null);
        setCreateError(undefined);
    };

    const create = async () => {
        if (newName === null) {
            setNewName('');
            return;
        }
        const name = newName.trim();
        if (!name || isCreating) {
            return;
        }
        const validation = validateName(name);
        if (validation) {
            setCreateError(validation);
            return;
        }
        setIsCreating(true);
        const created = await onCreateTag(name);
        setIsCreating(false);
        if (created) {
            closeCreate();
        }
    };

    return (
        <DamSidePanel title={t('DAM.LIBRARY.TAGS')} onClose={onClose}>
            <div className="tw-flex tw-flex-col tw-gap-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-divider tw-pb-2">
                    <span className="tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">{tagCount}</span>
                    <DamTagSortMenu />
                </div>

                <div className="tw-flex tw-flex-col tw-gap-2">
                    {newName !== null && (
                        <DamTagNameField
                            value={newName}
                            onChange={(value) => {
                                setNewName(value);
                                setCreateError(undefined);
                            }}
                            onSubmit={() => void create()}
                            onCancel={closeCreate}
                            error={createError}
                            disabled={isCreating}
                            autoFocus
                        />
                    )}
                    <Button
                        variant="outline"
                        hasIcon
                        icon={PlusIcon}
                        loading={isCreating}
                        disabled={isCreating || (newName !== null && !newName.trim())}
                        onClick={() => void create()}
                        className="tw-w-full"
                    >
                        {t('DAM.LIBRARY.CREATE_TAG')}
                    </Button>
                </div>

                {isLoading && (
                    <div className="tw-flex tw-justify-center tw-py-4">
                        <Loader />
                    </div>
                )}
                {!isLoading && sortedLabels.length === 0 && (
                    <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('DAM.LIBRARY.NO_TAGS')}</p>
                )}
                {!isLoading && visibleLabels.length > 0 && (
                    <ul className="tw-m-0 tw-list-none tw-p-0">
                        {visibleLabels.map((label) => (
                            <li key={label.id}>
                                <DamTagRow
                                    label={label}
                                    isSelected={selectedLabelIds.includes(label.id)}
                                    onToggle={() => onToggleLabel(label.id)}
                                    onRename={(name) => onRenameTag(label, name)}
                                    onDelete={() => onDeleteTag(label)}
                                    validateName={(name) => validateName(name, label.id)}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </DamSidePanel>
    );
}

export { DamTagsPanel };
export type { DamTagsPanelProps };
